import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import CheckboxGroup from '~components/forms/inputs/CheckboxCustom';
import Markdown from '~components/common/Markdown';
import { CustomizableLink } from '~components/common/Link';

/**
 * The interactive components of the subscriber's SMS disclaimer.
 * Omitted if there is no subscriber (i.e. we are on VoteAmerica's website
 * or a free tool embed).
 */
export function SmsOptInSubscriber({ errors }) {
  const sourcing = useSelector((state) => state.sourcing);
  const { subscriberData } = sourcing;

  /* If subscriber information is not undefined, iterate through the conditions of disclaimers */
  if (typeof subscriberData !== 'undefined') {
    /* SMS enabled, checkbox. Checkbox prechecked is in subscriberThunk.js */
    if (subscriberData.sms_enabled && subscriberData.sms_checkbox) {
      return (
        <div>
          <CheckboxGroup name='sms_opt_in_subscriber' errors={errors}>
            {subscriberData?.disclaimer ? (
              <Markdown content={subscriberData.disclaimer} />
            ) : (
              'Disclaimer'
            )}
          </CheckboxGroup>
        </div>
      );
    }
  }

  /* If there's no subscriber information, or the subscriber doesn't have a
     checkbox, return nothing */
  return null;
}

SmsOptInSubscriber.propTypes = {
  errors: PropTypes.object.isRequired,
};

/**
 * The interactive components of the subscriber's SMS disclaimer.
 */
export function SmsOptInVoteAmerica({ errors }) {
  return (
    <div>
      <CheckboxGroup name='sms_opt_in_voteamerica' errors={errors}>
        I agree to receive text messages from{' '}
        <span className='font-bold'>VoteAmerica</span>.
      </CheckboxGroup>
    </div>
  );
}

SmsOptInVoteAmerica.propTypes = {
  errors: PropTypes.object.isRequired,
};

/**
 * General disclaimer text, including links to terms and privacy policies.
 * Also explains that the user is opting in to receive emails from VoteAmerica.
 */
function Disclaimer({ hasSubscriberOptIn }) {
  const sourcing = useSelector((state) => state.sourcing);
  const { subscriberData } = sourcing;

  return (
    <div className='text-base'>
      <span className='font-bold'>
        Powered by{' '}
        <CustomizableLink
          href='https://www.voteamerica.org'
          target='_blank'
          rel='noopener'
          title='VoteAmerica'
        >
          VoteAmerica
        </CustomizableLink>
        .{' '}
      </span>
      By continuing, you accept VoteAmerica&apos;s{' '}
      <CustomizableLink
        href='/terms/'
        target='_blank'
        rel='noopener'
        title='VoteAmerica Terms Of Service'
      >
        Terms
      </CustomizableLink>{' '}
      and{' '}
      <CustomizableLink
        href='/privacy/'
        target='_blank'
        rel='noopener'
        title='VoteAmerica Privacy Policy'
      >
        Privacy Policy
      </CustomizableLink>
      . If you opted in to receive text messages from VoteAmerica
      {subscriberData &&
        subscriberData.name &&
        !subscriberData.is_first_party &&
        hasSubscriberOptIn && <> and/or {subscriberData.name}</>}
      , message and data rates may apply. Message frequency varies. Reply STOP
      to unsubscribe from texts or HELP for more info.
      {subscriberData &&
        subscriberData.privacy_policy &&
        !subscriberData.is_first_party && (
          <>
            {' '}
            <CustomizableLink
              href={subscriberData.privacy_policy}
              target='_blank'
              rel='noopener'
              title={`${subscriberData.name} privacy policy`}
            >
              {subscriberData.name} Privacy Policy
            </CustomizableLink>
            .
          </>
        )}
    </div>
  );
}

Disclaimer.propTypes = {
  hasSubscriberOptIn: PropTypes.bool,
};

/**
 * General disclaimer text for situations where we are not asking for an email address,
 * including links to terms and privacy policies.
 */
export function PhoneOnlyDisclaimerVoteAmerica() {
  return (
    <div className='text-base'>
      If you opted in to receive text messages from VoteAmerica, message and
      data rates may apply. Message frequency varies. Reply STOP to unsubscribe
      from texts or HELP for more info.
    </div>
  );
}

export function SmsOptAndDisclaimer({ hasSubscriberOptIn, errors }) {
  return (
    <>
      {hasSubscriberOptIn && <SmsOptInSubscriber errors={errors} />}
      <SmsOptInVoteAmerica errors={errors} />
      <Disclaimer hasSubscriberOptIn={hasSubscriberOptIn} />
    </>
  );
}

SmsOptAndDisclaimer.propTypes = {
  hasSubscriberOptIn: PropTypes.bool,
  errors: PropTypes.object.isRequired,
};

SmsOptAndDisclaimer.defaultProps = {
  hasSubscriberOptIn: true,
};
